import * as ReactDOMClient from 'react-dom/client';
import './index.css';
import App from './containers/App';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import Keycloak from 'keycloak-js';
import axios from 'axios';
import {
  SystemNotificationProvider,
  showGlobalNotification,
} from './components/SystemNotification';

const root = ReactDOMClient.createRoot(document.getElementById('root'));

const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const isInContainer = window.location.port === '';

const keycloakUri = !isLocalhost
  ? `${window.location.protocol}//${window.location.hostname}/keycloak/`
  : isInContainer
    ? 'http://localhost/keycloak/'
    : 'http://localhost/keycloak/';

const apiUri = !isLocalhost
  ? `${window.location.protocol}//${window.location.hostname}/api/flags/`
  : isInContainer
    ? 'http://localhost/api/flags/'
    : 'http://localhost:3010/flags/';

const defaultSettings = {
  url: keycloakUri,
  realm: 'cra',
  clientId: 'cra',
};

const getFeatureFlags = async () => {
  try {
    const { data } = await axios.get(apiUri, { timeout: 5000 });
    return { success: true, data: data ?? {} };
  } catch (err) {
    console.error('[X] Error fetching feature flags', err);
    return {
      success: false,
      error:
        err.code === 'ECONNABORTED' || err.message.includes('timeout')
          ? 'Cannot connect to API server - request timeout'
          : 'Cannot connect to API server',
    };
  }
};

const applyOverrides = (flags, defaultSettings) => {
  const settings = { ...defaultSettings };
  // Prefer the browser-reachable URL; KEYCLOAK_SERVER_URL may be docker-internal
  if (flags.KEYCLOAK_PUBLIC_URL) settings.url = flags.KEYCLOAK_PUBLIC_URL;
  else if (flags.KEYCLOAK_SERVER_URL) settings.url = flags.KEYCLOAK_SERVER_URL;
  if (flags.KEYCLOAK_REALM) settings.realm = flags.KEYCLOAK_REALM;
  return settings;
};

export let authInstance;

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 5000;
let retryCount = 0;

async function start() {
  const result = await getFeatureFlags();
  if (!result.success) {
    retryCount++;

    if (retryCount > MAX_RETRIES) {
      root.render(
        <SystemNotificationProvider>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw',
              flexDirection: 'column',
              gap: '1rem',
              color: '#666',
            }}
          >
            <p style={{ margin: 0, fontSize: '1.2rem' }}>
              Unable to connect to the server.
            </p>
            <p style={{ margin: 0 }}>Please check your connection and reload the page.</p>
          </div>
        </SystemNotificationProvider>
      );
      return;
    }

    root.render(
      <SystemNotificationProvider>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <div
            style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              animation: 'spin 1s linear infinite',
            }}
          ></div>
          <p style={{ margin: 0, color: '#666' }}>Loading...</p>
          <p style={{ margin: 0, color: '#999', fontSize: '0.85rem' }}>
            Retrying... (attempt {retryCount} of {MAX_RETRIES})
          </p>
          <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
        </div>
      </SystemNotificationProvider>
    );

    showGlobalNotification('error', result.error, 'Network problem');

    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount - 1), 60000);
    setTimeout(() => start(), delay);

    return;
  }

  retryCount = 0;
  const settings = applyOverrides(result.data, defaultSettings);

  // log to check keycloak settings
  // console.log('Keycloak settings:', settings);

  authInstance = new Keycloak(settings);

  root.render(
    <SystemNotificationProvider>
      <ReactKeycloakProvider
        authClient={authInstance}
        initOptions={{
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        }}
        onEvent={(event) => {
          if (event === 'onReady') {
            console.log('Authentication ready...');
          }
        }}
        LoadingComponent={<></>}
      >
        <App loggedIn={authInstance}></App>
      </ReactKeycloakProvider>
    </SystemNotificationProvider>
  );
}

start();
